import { createElement, useEffect, useState, type ReactNode } from 'react'

/**
 * Render a shared .docx in the meeting.
 *
 * A browser has no Word viewer, so the choice is: convert somewhere, or show
 * nothing. This converts in the page — the file never leaves the participant's
 * machine, which the alternatives (Office Online, Google's viewer) cannot say:
 * both need the document fetchable by a third party, and every share would be
 * handed to them.
 *
 * Legacy `.doc` is NOT covered. It is a binary OLE container, not a zip of XML,
 * and nothing converts it client-side. That one needs either a server-side
 * conversion step or an external viewer — a decision with a privacy cost, so
 * it is not made here quietly.
 */

/** Does this share look like a Word .docx? */
export function isDocxShare(mime: string, filename?: string | null): boolean {
  const m = (mime || '').toLowerCase().split(';')[0].trim()
  if (m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true
  return /\.docx(?:[?#]|$)/i.test(filename ?? '')
}

/** Legacy binary Word. Detected only so it can be reported honestly instead of
 *  failing as an unexplained blank frame. */
export function isLegacyDocShare(mime: string, filename?: string | null): boolean {
  const m = (mime || '').toLowerCase().split(';')[0].trim()
  if (m === 'application/msword') return true
  return /\.doc(?:[?#]|$)/i.test(filename ?? '')
}

/**
 * Elements mammoth is allowed to produce. Anything else is dropped and its
 * children kept, so unknown markup degrades to its text rather than vanishing.
 */
export function isAllowedTag(tag: string): boolean {
  return ALLOWED_TAGS.has(tag.toLowerCase())
}

const ALLOWED_TAGS = new Set([
  'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 's',
  'blockquote', 'sup', 'sub', 'a', 'img',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
])

/** Only a link may keep an href, and only to somewhere inert. */
export function safeHref(raw: string | null): string | undefined {
  if (!raw) return undefined
  const v = raw.trim()
  return /^(https?:|mailto:)/i.test(v) ? v : undefined
}

/**
 * Mammoth inlines a document's images as data URIs. Anything else is not an
 * image we made — a remote `src` would let a shared document report who opened
 * it and when, to whoever wrote the document.
 *
 * SVG is excluded even though it arrives as `data:image/`. An `<img>` does not
 * run script in an SVG, so this is belt-and-braces rather than a live hole —
 * but the images inside a .docx are raster (png/jpeg/emf), so nothing is lost
 * by not depending on that rule holding in every engine.
 */
export function safeSrc(raw: string | null): string | undefined {
  if (!raw) return undefined
  const v = raw.trim()
  if (/^data:image\/svg/i.test(v)) return undefined
  return /^data:image\//i.test(v) ? v : undefined
}

/**
 * Convert the parsed document to React elements.
 *
 * Deliberately NOT `dangerouslySetInnerHTML`. The HTML here is derived from a
 * file a participant supplied, so injecting it as markup would let a crafted
 * document run script in this origin — with the meeting's tokens in reach.
 * Building elements means nothing is ever parsed as HTML a second time, and the
 * allowlist above is the whole attack surface.
 */
function toReact(node: Node, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent
  if (node.nodeType !== Node.ELEMENT_NODE) return null

  const el = node as Element
  const tag = el.tagName.toLowerCase()
  const children = Array.from(el.childNodes).map((c, i) => toReact(c, i))

  if (!ALLOWED_TAGS.has(tag)) return createElement('span', { key }, ...children)

  if (tag === 'br') return createElement('br', { key })
  if (tag === 'img') {
    const src = safeSrc(el.getAttribute('src'))
    if (!src) return null
    return createElement('img', {
      key,
      src,
      alt: el.getAttribute('alt') ?? '',
      className: 'max-w-full h-auto my-2',
    })
  }
  if (tag === 'a') {
    const href = safeHref(el.getAttribute('href'))
    return createElement(
      'a',
      { key, href, target: '_blank', rel: 'noopener noreferrer', className: 'text-sky-700 underline' },
      ...children,
    )
  }
  return createElement(tag, { key }, ...children)
}

export function useSharedDocx(
  objectUrl: string,
  enabled: boolean,
): { content: ReactNode | null; loading: boolean; error: string | null } {
  const [content, setContent] = useState<ReactNode | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!objectUrl || !enabled) {
      setContent(null)
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      try {
        // Imported on demand: the converter is only needed by whoever opens a
        // Word document, and it is not small enough to sit in the entry chunk
        // of a video call.
        const mammoth = await import('mammoth')
        const buf = await (await fetch(objectUrl)).arrayBuffer()
        const { value } = await mammoth.convertToHtml({ arrayBuffer: buf })
        if (cancelled) return
        const doc = new DOMParser().parseFromString(value, 'text/html')
        setContent(Array.from(doc.body.childNodes).map((n, i) => toReact(n, i)))
      } catch (err: unknown) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Could not read this Word document.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [objectUrl, enabled])

  return { content, loading, error }
}
